// ============================================
// Path: /frontend/src/pages/admin/MarketingPage.jsx
// Purpose: Admin page for marketing campaigns and promotional features
// ============================================

// ========================================
// IMPORTS
// ========================================
import { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Loader2, Send, Star, ImagePlus } from 'lucide-react';
import { useToast } from '../../hooks/useToast';

// ========================================
// MAIN COMPONENT
// ========================================
export default function MarketingPage() {
  console.log('🎨 [MarketingPage] Component mounted');

  const { toast } = useToast();
  const [isSending, setIsSending] = useState(false);

  // ========================================
  // EVENT HANDLERS
  // ========================================
  const handleSendPromotion = (e) => {
    e.preventDefault();
    console.log('👤 [MarketingPage] Sending promotion...');
    setIsSending(true);

    // Simulate an API call
    setTimeout(() => {
      console.log('✅ [MarketingPage] Promotion sent');
      setIsSending(false);
      toast({
        title: 'Promotion Sent!',
        description: 'Your email campaign has been dispatched.',
      });
    }, 1500);
  };

  const handleGenericAction = (name) => {
    console.log('👤 [MarketingPage] Generic action:', name);
    toast({
      title: 'Functionality Not Implemented',
      description: `${name} has not been implemented yet.`,
    });
  };

  // ========================================
  // RENDER
  // ========================================
  return (
    <div className="space-y-8">
      <h1 className="text-3xl font-bold">Marketing & Promotions</h1>

      <div className="grid gap-8 lg:grid-cols-2">
        {/* Email Campaign Creator */}
        <Card>
          <CardHeader>
            <CardTitle>Email Campaign Creator</CardTitle>
            <CardDescription>Create and send promotional emails to your users.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSendPromotion} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="campaign-name">Campaign Name</Label>
                <Input id="campaign-name" placeholder="e.g., Summer Sale 2024" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="target-audience">Target Audience</Label>
                <Select>
                  <SelectTrigger id="target-audience">
                    <SelectValue placeholder="Select audience" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Users</SelectItem>
                    <SelectItem value="buyers">All Buyers</SelectItem>
                    <SelectItem value="sellers">All Sellers</SelectItem>
                    <SelectItem value="new-users">New Users (Last 30 Days)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email-subject">Email Subject</Label>
                <Input id="email-subject" placeholder="e.g., Get 20% Off Your Next Listing!" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email-body">Email Body</Label>
                <Textarea
                  id="email-body"
                  rows={8}
                  placeholder="Compose your message. You can include details about promotions, discounts, or free listings for referrals..."
                />
              </div>
              <Button type="submit" disabled={isSending}>
                {isSending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Send className="mr-2 h-4 w-4" />
                )}
                Send Promotion
              </Button>
            </form>
          </CardContent>
        </Card>

        <div className="space-y-8">
          {/* Featured Listings Management */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Star className="w-5 h-5" /> Manage Featured Listings
              </CardTitle>
              <CardDescription>
                Promote specific listings to the homepage or top of search results.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Label htmlFor="feature-listing">Enter Listing ID to Feature</Label>
                <div className="flex gap-2">
                  <Input id="feature-listing" placeholder="e.g., LsT1nGiD123" />
                  <Button onClick={() => handleGenericAction('Featuring listings')}>Feature</Button>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mt-4">
                Featured listings management UI will be here.
              </p>
            </CardContent>
          </Card>

          {/* Banner Ad Management */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ImagePlus className="w-5 h-5" /> Banner Ad Management
              </CardTitle>
              <CardDescription>Sell ad space by managing banner advertisements.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="banner-image">Upload Banner Creative</Label>
                  <Input id="banner-image" type="file" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="banner-link">Banner Link</Label>
                  <Input id="banner-link" placeholder="https://example.com/product" />
                </div>
                <Button onClick={() => handleGenericAction('Banner upload')}>Upload Banner</Button>
              </div>
              <p className="text-sm text-muted-foreground mt-4">
                A list of active banners and their performance will be shown here.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}